import { isDefinedError, safe } from "@orpc/client"
import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import {
	CheckCircle2,
	Download,
	FileSpreadsheet,
	HelpCircle,
	History,
	Landmark,
	Loader2,
	Lock,
	RefreshCw,
	Scale,
	ShieldCheck,
	ShoppingCart,
	Ticket,
	TrendingUp,
	Unlock,
	Wallet,
	XCircle,
} from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	AttentionCount,
	closeReconciliationPresentation,
	closeStatusPresentation,
	deltaClass,
	FLUSH_TABLE_CLASS,
	Money,
	SkeletonRows,
	StatusView,
	SummaryGroup,
	SummaryRow,
	WRAPPING_PAGE_HEADER_CLASS,
} from "@/components/accounting/accounting-ui"
import {
	EMPTY_VALUE,
	formatEur,
	formatRate,
	periodKey,
} from "@/components/accounting/reconciliation-format"
import {
	DetailPanel,
	PageHeader,
	Panel,
	Stat,
	StatStrip,
} from "@/components/layout"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import {
	useEvaluateMonthlyCloseGate,
	useFinaliseMonthlyClose,
	useMonthlyCloseDetail,
	useRegenerateMonthlyClose,
	useReopenMonthlyClose,
} from "@/queries/marketplace/use-monthly-closes"

/** Every check the finalisation gate runs, in the order design-close-workbook.md §1.6 lists them. */
const GATE_CHECKS: ReadonlyArray<{ code: string; label: string }> = [
	{
		code: "MONTH_NOT_ENDED",
		label: "Month has ended in every seller timezone",
	},
	{
		code: "PREVIOUS_MONTH_NOT_FINALISED",
		label: "Previous month is finalised",
	},
	{
		code: "LEDGER_NOT_SYNCED",
		label: "Stripe ledger synced through month end + 48h",
	},
	{
		code: "ANCHOR_MISMATCH",
		label: "Ledger anchor ties to the live Stripe balance",
	},
	{
		code: "ROLLFORWARD_BREAK",
		label: "Daily balances roll forward with no break",
	},
	{
		code: "OPEN_EXCEPTIONS",
		label: "No open reconciliation exceptions in the month",
	},
	{
		code: "VAT_LINES_UNAVAILABLE",
		label: "VAT lines are available for the month",
	},
	{
		code: "DRAFT_STALE",
		label: "Draft reflects the current ledger and VAT lines",
	},
	{
		code: "INTERNAL_TIE_OUT_FAILED",
		label: "Balance by status and refund totals tie out",
	},
]

export const Route = createFileRoute("/accounting/monthly-closes/$closeId")({
	component: MonthlyCloseDetailPage,
})

function MonthlyCloseDetailPage() {
	const { closeId } = useParams({ from: "/accounting/monthly-closes/$closeId" })
	const detail = useMonthlyCloseDetail(closeId)
	const regenerate = useRegenerateMonthlyClose()
	const finalise = useFinaliseMonthlyClose()
	const evaluateGate = useEvaluateMonthlyCloseGate()
	const reopen = useReopenMonthlyClose()
	const { isSuperadmin } = useAdminRolesContext()
	const [reopenOpen, setReopenOpen] = React.useState(false)
	const [reopenReason, setReopenReason] = React.useState("")

	if (detail.isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-7 w-64" />
				<Panel>
					<SkeletonRows rows={6} />
				</Panel>
			</div>
		)
	}
	if (detail.isError) {
		return (
			<div className="p-4 sm:p-6">
				<p className="rounded-lg bg-bad-soft p-4 text-bad">
					Failed to load close
					{detail.error instanceof Error ? `: ${detail.error.message}` : "."}
				</p>
			</div>
		)
	}
	if (!detail.data) {
		return <p className="p-4 text-muted-foreground sm:p-6">Close not found.</p>
	}
	const c = detail.data
	const generating =
		c.generationStatus === "queued" || c.generationStatus === "running"
	const gateFailureByCode = new Map<string, { code: string; message: string }>(
		(c.gateResult?.failures ?? []).map((f) => [f.code as string, f]),
	)
	const gatePassing = c.gateResult?.passed === true

	const handleRegenerate = async () => {
		const [error] = await safe(regenerate.mutateAsync(closeId))
		if (isDefinedError(error)) {
			const e = error as { code: string }
			if (e.code === "CLOSE_FINALISED") {
				toast.error("Close is finalised; it cannot be regenerated.")
				return
			}
			if (e.code === "REGENERATION_IN_PROGRESS") {
				toast.error("A regeneration is already queued or running.")
				return
			}
		}
		if (error) {
			toast.error("Could not queue the regeneration.")
			return
		}
		toast.success(
			"Regeneration queued. The page refreshes when the new version is ready.",
		)
	}

	const handleFinalise = async () => {
		const [error] = await safe(finalise.mutateAsync(closeId))
		if (isDefinedError(error)) {
			const e = error as { code: string }
			if (e.code === "NO_VERSION_GENERATED") {
				toast.error("Generate a version before finalising.")
				return
			}
			if (e.code === "ONLY_DRAFT_CAN_BE_FINALISED") {
				toast.error("Only a draft can be finalised.")
				return
			}
			if (e.code === "REGENERATION_IN_PROGRESS") {
				toast.error(
					"A regeneration is queued or running. Wait for it to finish before finalising.",
				)
				return
			}
			if (e.code === "GATE_FAILED") {
				toast.error(
					"The finalisation gate failed. Check the panel below for which checks did not pass.",
				)
				return
			}
			if (e.code === "VAT_FREEZE_FAILED") {
				toast.error("Freezing the VAT lines for this month failed.")
				return
			}
			if (e.code === "DRAFT_STALE") {
				toast.error(
					"The VAT lines changed since this draft was generated. Regenerate and try again.",
				)
				return
			}
			if (e.code === "WORKBOOK_REGENERATION_FAILED") {
				toast.error(
					"Could not build or upload the final workbook. The close was not finalised. Try again.",
				)
				return
			}
		}
		if (error) {
			toast.error("Finalise failed.")
			return
		}
		toast.success("Close finalised.")
	}

	const handleCheckReadiness = async () => {
		const [error] = await safe(evaluateGate.mutateAsync(closeId))
		if (error) {
			toast.error("Could not re-run the finalisation gate.")
			return
		}
		toast.success("Readiness refreshed.")
	}

	const handleReopen = async () => {
		const [error] = await safe(
			reopen.mutateAsync({ closeId, reason: reopenReason.trim() }),
		)
		if (isDefinedError(error)) {
			const e = error as {
				code: string
				data?: { year?: number; month?: number }
			}
			if (e.code === "ONLY_FINALISED_CAN_BE_REOPENED") {
				toast.error("Only a finalised close can be reopened.")
				return
			}
			if (e.code === "LATER_MONTH_FINALISED") {
				toast.error(
					`Reopen ${e.data?.year}-${String(e.data?.month ?? 0).padStart(2, "0")} first: months reopen from the latest finalised one backwards.`,
				)
				return
			}
		}
		if (error) {
			toast.error("Reopen failed.")
			return
		}
		setReopenOpen(false)
		setReopenReason("")
		toast.success(
			"Close reopened as a draft. Regenerate to produce the next version.",
		)
	}

	const period = periodKey(c.year, c.month)
	const grossRevenueEur =
		c.buyerFeesCollectedEur +
		c.sellerFeesCollectedEur +
		c.currencyConversionFeeEur +
		c.shippingMarginEur

	return (
		<TooltipProvider>
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<PageHeader
					className={WRAPPING_PAGE_HEADER_CLASS}
					title={
						<span className="inline-flex items-center gap-3">
							<span className="font-mono">
								{c.region} · {period}
							</span>
							<StatusView
								presentation={closeStatusPresentation(c.status)}
								className="text-sm font-medium tracking-normal"
							/>
						</span>
					}
					description={
						<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<StatusView
								presentation={{
									...closeReconciliationPresentation(c.reconciliationStatus),
									label: `Reconciliation ${closeReconciliationPresentation(c.reconciliationStatus).label.toLowerCase()}`,
								}}
							/>
							{generating && (
								<StatusDot tone="info">
									<Loader2 className="size-3 animate-spin" />
									{c.generationStatus === "queued"
										? "Regeneration queued"
										: "Regenerating"}
								</StatusDot>
							)}
							{c.generationStatus === "failed" && (
								<StatusTag tone="bad" title={c.generationError}>
									Last generation failed
								</StatusTag>
							)}
							{c.inputsChanged && !generating && (
								<StatusTag tone="warn">Inputs changed, regenerate</StatusTag>
							)}
						</span>
					}
					actions={
						<>
							{c.downloadUrl && (
								<Button asChild variant="outline">
									<a
										href={c.downloadUrl}
										target="_blank"
										rel="noreferrer"
										download={c.filename}
									>
										<Download /> Download XLSX
									</a>
								</Button>
							)}
							{c.status === "draft" && (
								<Button
									variant="outline"
									onClick={handleRegenerate}
									disabled={regenerate.isPending || generating}
								>
									{generating ? (
										<Loader2 className="animate-spin" />
									) : (
										<RefreshCw />
									)}
									Regenerate
								</Button>
							)}
							{c.status === "finalised" && isSuperadmin && (
								<Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
									<DialogTrigger asChild>
										<Button variant="outline" disabled={reopen.isPending}>
											<Unlock />
											Reopen
										</Button>
									</DialogTrigger>
									<DialogContent>
										<DialogHeader>
											<DialogTitle>
												Reopen {c.region} {period}?
											</DialogTitle>
											<DialogDescription>
												The close goes back to draft and keeps version{" "}
												{c.version}. Regenerate afterwards to produce the next
												version, then finalise again. The reason is logged in
												the close's reopen history.
											</DialogDescription>
										</DialogHeader>
										<Textarea
											value={reopenReason}
											onChange={(e) => setReopenReason(e.target.value)}
											placeholder="Why is this month being reopened?"
											rows={3}
										/>
										<DialogFooter>
											<Button
												variant="outline"
												onClick={() => setReopenOpen(false)}
											>
												Cancel
											</Button>
											<Button
												onClick={handleReopen}
												disabled={
													reopen.isPending || reopenReason.trim().length < 3
												}
											>
												<Unlock />
												Reopen close
											</Button>
										</DialogFooter>
									</DialogContent>
								</Dialog>
							)}
							{c.status === "draft" && (
								<AlertDialog>
									<Tooltip>
										<TooltipTrigger asChild>
											<span>
												<AlertDialogTrigger asChild>
													<Button
														disabled={
															finalise.isPending || generating || !gatePassing
														}
													>
														<Lock />
														Finalise
													</Button>
												</AlertDialogTrigger>
											</span>
										</TooltipTrigger>
										{!gatePassing && (
											<TooltipContent>
												Every check in the finalisation gate must pass first.
											</TooltipContent>
										)}
									</Tooltip>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												Finalise {c.region} {period}?
											</AlertDialogTitle>
											<AlertDialogDescription>
												Finalising locks this close as the source of truth for
												the period. Numbers stop refreshing and only a
												superadmin reopen can flip it back to draft (logged in
												the close's reopen history). Make sure the workbook ties
												out before continuing.
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>Cancel</AlertDialogCancel>
											<AlertDialogAction onClick={handleFinalise}>
												<Lock />
												Finalise close
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							)}
						</>
					}
				/>

				<StatStrip>
					<Stat
						label={
							<HoverHead tooltip="Gross GMV − Refunded GMV. Cancelled orders aren't in Gross.">
								Net GMV
							</HoverHead>
						}
						value={formatEur(c.netGmvEur)}
						detail="Gross − Refunded, in-period"
					/>
					<Stat
						label={
							<HoverHead tooltip="Sum of charged amounts for orders that successfully settled to a seller payout this period. Excludes cancellations and full refunds. Partial refunds remain (the kept portion contributes; the refunded portion is on the Refunded line).">
								Settled GMV
							</HoverHead>
						}
						value={formatEur(c.gmvEur)}
						detail={`${c.settledOrderCount} order${c.settledOrderCount === 1 ? "" : "s"}`}
					/>
					<Stat
						label={
							<HoverHead tooltip="Buyer fees + seller fees + 3% currency conversion fee on cross-currency orders + shipping margin on managed (SendCloud) orders. Headline platform revenue before any deductions.">
								Gross revenue
							</HoverHead>
						}
						value={formatEur(grossRevenueEur)}
						detail="Buyer + seller fees + conv. fee + ship margin"
					/>
					<Stat
						label={
							<HoverHead tooltip="Gross revenue − coupons and promotions paid by CardNexus − Stripe processing fees. What we actually keep after platform-funded discounts and payment-processing costs.">
								Net revenue
							</HoverHead>
						}
						value={formatEur(c.netPlatformRevenueEur)}
						detail="Gross − discounts − Stripe fees"
					/>
					<Stat
						label={
							<HoverHead tooltip="Retained buyer-paid shipping − the carrier label cost we paid, on managed (SendCloud) orders. Seller-shipped orders contribute 0 (shipping flows to the seller, no label cost).">
								Shipping margin
							</HoverHead>
						}
						value={formatEur(c.shippingMarginEur)}
						detail={`Buyer-paid − label cost (${formatEur(c.carrierLabelCostEur)} cost)`}
					/>
				</StatStrip>

				<div className="flex min-w-0 flex-col gap-4">
					<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
						<div className="flex min-w-0 flex-col gap-4">
							{c.stuckLiabilityCount > 0 && (
								<div className="flex flex-col gap-0.5 rounded-lg bg-bad-soft px-4 py-3">
									<span className="font-medium text-bad tabular-nums">
										{c.stuckLiabilityCount} stuck liabilit
										{c.stuckLiabilityCount === 1 ? "y" : "ies"},{" "}
										{formatEur(c.stuckLiabilityAmountEur)}
									</span>
									<span className="text-muted-foreground">
										Orders past their payout hold at the period end without a
										seller transfer. Investigate in the workbook's "Awaiting
										payout at T" tab.
									</span>
								</div>
							)}

							<DetailPanel
								title="Finalisation gate"
								icon={ShieldCheck}
								wrapActions
								meta={
									c.gateEvaluatedAt
										? `Last evaluated ${new Date(c.gateEvaluatedAt).toLocaleString()}`
										: "Not evaluated yet. The nightly tick evaluates the previous month, and finalising re-evaluates it."
								}
								actions={
									<>
										<Button asChild variant="ghost" size="sm">
											<Link
												to="/accounting/reconciliation/$region/$period"
												params={{ region: c.region, period }}
											>
												View reconciliation
											</Link>
										</Button>
										{c.status === "draft" && (
											<Button
												variant="outline"
												size="sm"
												onClick={handleCheckReadiness}
												disabled={evaluateGate.isPending || generating}
											>
												{evaluateGate.isPending ? (
													<Loader2 className="animate-spin" />
												) : (
													<ShieldCheck />
												)}
												Check readiness now
											</Button>
										)}
									</>
								}
								flush
							>
								<ul className="m-0 flex list-none flex-col divide-y p-0">
									{GATE_CHECKS.map((check) => {
										const failure = gateFailureByCode.get(check.code)
										const passed = !failure && c.gateEvaluatedAt != null
										return (
											<li
												key={check.code}
												className="flex min-h-9 items-start gap-2.5 px-4 py-2"
											>
												{!c.gateEvaluatedAt ? (
													<HelpCircle className="mt-0.5 size-3.5 shrink-0 text-meta" />
												) : passed ? (
													<CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-ok" />
												) : (
													<XCircle className="mt-0.5 size-3.5 shrink-0 text-bad" />
												)}
												<div className="flex min-w-0 flex-col">
													<span>{check.label}</span>
													{failure && (
														<span className="text-xs text-bad">
															{failure.message}
														</span>
													)}
												</div>
											</li>
										)
									})}
								</ul>
							</DetailPanel>

							{c.reconciliationStatementSummary && (
								<DetailPanel
									title="Reconciliation statement"
									icon={Scale}
									badges={
										c.reconciliationStatementSummary.openExceptions > 0 ? (
											<StatusTag tone="bad">
												{c.reconciliationStatementSummary.openExceptions} open
												exception
												{c.reconciliationStatementSummary.openExceptions === 1
													? ""
													: "s"}
											</StatusTag>
										) : (
											<StatusDot tone="ok">No open exceptions</StatusDot>
										)
									}
									meta={`Frozen at finalisation, ${new Date(
										c.reconciliationStatementSummary.generatedAt,
									).toLocaleString()}`}
									flush
								>
									<Table className={FLUSH_TABLE_CLASS}>
										<TableHeader>
											<TableRow>
												<TableHead>Currency</TableHead>
												<TableHead>Balance</TableHead>
												<TableHead className="text-right">Opening</TableHead>
												<TableHead className="text-right">Closing</TableHead>
												<TableHead className="text-right">
													Roll-forward delta
												</TableHead>
												<TableHead className="text-right">
													Open exceptions
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{c.reconciliationStatementSummary.blocks.map((block) => (
												<TableRow
													key={`${block.currency}-${block.balanceType}`}
												>
													<TableCell className="font-mono">
														{block.currency}
													</TableCell>
													<TableCell className="font-mono text-muted-foreground">
														{block.balanceType}
													</TableCell>
													<TableCell className="text-right">
														<Money
															cents={block.opening}
															currency={block.currency}
														/>
													</TableCell>
													<TableCell className="text-right">
														<Money
															cents={block.closing}
															currency={block.currency}
														/>
													</TableCell>
													<TableCell className="text-right">
														<Money
															cents={block.rollForwardDelta}
															currency={block.currency}
															className={deltaClass(block.rollForwardDelta)}
														/>
													</TableCell>
													<TableCell className="text-right">
														<AttentionCount count={block.openExceptions} />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</DetailPanel>
							)}

							<DetailPanel
								title="P&L"
								icon={TrendingUp}
								meta="Every amount in EUR, at each line's own conversion rate. See the workbook's P&L tab for the full methodology."
								flush
							>
								{!c.pnlLines || c.pnlLines.length === 0 ? (
									<p className="px-4 py-3 text-meta">
										Not available. Regenerate this close to compute it.
									</p>
								) : (
									<Table className={FLUSH_TABLE_CLASS}>
										<TableHeader>
											<TableRow>
												<TableHead>Line</TableHead>
												<TableHead className="text-right">
													Amount (EUR)
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{c.pnlLines.map((line) => (
												<TableRow key={line.key}>
													<TableCell>{line.label}</TableCell>
													<TableCell className="text-right">
														<Money cents={line.amountEur} currency="EUR" />
													</TableCell>
												</TableRow>
											))}
											<TableRow className="font-semibold">
												<TableCell>Net platform revenue</TableCell>
												<TableCell className="text-right">
													<Money
														cents={c.pnlLines.reduce(
															(sum, line) => sum + line.amountEur,
															0,
														)}
														currency="EUR"
													/>
												</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								)}
							</DetailPanel>

							<DetailPanel
								title="Balance by order status"
								icon={Wallet}
								meta="Entity-level EUR view of the Stripe balance at T, by order status and liability."
								flush
							>
								{!c.balanceByStatus ||
								c.balanceByStatus.economicView.length === 0 ? (
									<p className="px-4 py-3 text-meta">
										Not available. Regenerate this close to compute it.
									</p>
								) : (
									<Table className={FLUSH_TABLE_CLASS}>
										<TableHeader>
											<TableRow>
												<TableHead>Line</TableHead>
												<TableHead className="text-right">
													Amount (EUR)
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{c.balanceByStatus.economicView.map((line) => (
												<TableRow
													key={line.label}
													className={
														line.label === "Platform retained"
															? "font-semibold"
															: undefined
													}
												>
													<TableCell>{line.label}</TableCell>
													<TableCell className="text-right">
														<Money cents={line.amountEur} currency="EUR" />
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</DetailPanel>
						</div>

						<div className="flex min-w-0 flex-col gap-4">
							<DetailPanel title="Close" icon={FileSpreadsheet}>
								<SummaryGroup>
									<SummaryRow label="Version">
										<span className="font-mono">v{c.version}</span>
										{c.finalisedVersion != null &&
										c.finalisedVersion !== c.version ? (
											<span className="font-mono text-meta">
												{" "}
												(finalised v{c.finalisedVersion})
											</span>
										) : null}
									</SummaryRow>
									{c.methodologyVersion ? (
										<SummaryRow label="Methodology">
											<span className="font-mono">{c.methodologyVersion}</span>
										</SummaryRow>
									) : null}
									<SummaryRow label="Generated">
										{new Date(c.generatedAt).toLocaleString()}
									</SummaryRow>
									{c.timezone ? (
										<SummaryRow label="Period in">{c.timezone}</SummaryRow>
									) : null}
									{c.contentHash ? (
										<SummaryRow label="Content hash">
											<span className="font-mono">
												{c.contentHash.slice(0, 16)}…
											</span>
										</SummaryRow>
									) : null}
								</SummaryGroup>
							</DetailPanel>

							<DetailPanel title="GMV" icon={ShoppingCart}>
								<SummaryGroup>
									<SummaryRow
										label="Gross GMV"
										detail={`${c.grossOrderCount} fulfilled / in-flight`}
									>
										<Money cents={c.grossGmvEur} currency="EUR" />
									</SummaryRow>
									<SummaryRow
										label="Refunded"
										detail={`${c.refundedCount} event${c.refundedCount === 1 ? "" : "s"}`}
									>
										<Money
											cents={c.refundedGmvEur === 0 ? 0 : -c.refundedGmvEur}
											currency="EUR"
										/>
									</SummaryRow>
									<SummaryRow label="Net GMV" total>
										<Money cents={c.netGmvEur} currency="EUR" />
									</SummaryRow>
									<SummaryRow
										label="Cancelled (informational)"
										detail={`${c.cancelledOrderCount} pre-fulfillment, not in Gross`}
									>
										<Money
											cents={c.cancelledGmvEur}
											currency="EUR"
											className="text-meta"
										/>
									</SummaryRow>
								</SummaryGroup>
							</DetailPanel>

							<DetailPanel title="Discounts" icon={Ticket}>
								<SummaryGroup>
									<SummaryRow label="Coupons" detail="Paid by CardNexus">
										<Money cents={c.couponBurnEur} currency="EUR" />
									</SummaryRow>
									<SummaryRow
										label="Buyer promotions"
										detail="Discounts paid by CardNexus"
									>
										<Money cents={c.buyerPromotionBurnEur} currency="EUR" />
									</SummaryRow>
									<SummaryRow
										label="Shield from seller promotions"
										detail="Premiums CardNexus paid"
									>
										<Money cents={c.shieldPromotionBurnEur} currency="EUR" />
									</SummaryRow>
									<SummaryRow
										label="Buyer fees not charged (memo)"
										detail="Waived by buyer promotions, never revenue"
									>
										<Money
											cents={c.buyerFeesWaivedEur}
											currency="EUR"
											className="text-meta"
										/>
									</SummaryRow>
								</SummaryGroup>
							</DetailPanel>
						</div>
					</div>

					<DetailPanel title="VAT collected this period" icon={Landmark} flush>
						{c.vatCollected.length === 0 ? (
							<p className="px-4 py-3 text-meta">No VAT collected.</p>
						) : (
							<Table className={FLUSH_TABLE_CLASS}>
								<TableHeader>
									<TableRow>
										<TableHead>Mechanism</TableHead>
										<TableHead>Country</TableHead>
										<TableHead>
											<HoverHead tooltip="What the VAT was charged on. `goods` = sales VAT on the merchandise (IOSS / UK OMP / Stripe Tax NA). `buyer fee` = VAT on the platform fee paid by the buyer. `seller fee` = VAT on the commission paid by the seller. Total VAT due to a (mechanism, country) is the sum across bases.">
												Basis
											</HoverHead>
										</TableHead>
										<TableHead>
											<HoverHead tooltip="Currency of the filing. HMRC files in GBP, ESTV in CHF, Union OSS / IOSS / FR domestic in EUR. Independent of what the buyer paid in.">
												Filed in
											</HoverHead>
										</TableHead>
										<TableHead className="text-right">Rate</TableHead>
										<TableHead className="text-right">
											<HoverHead tooltip="Sum of fees / goods that were subject to this VAT, in the jurisdiction's reporting currency.">
												Taxable base (local)
											</HoverHead>
										</TableHead>
										<TableHead className="text-right">
											VAT collected (local)
										</TableHead>
										<TableHead className="text-right">
											<HoverHead tooltip="EUR-equivalent of the VAT collected, for cross-jurisdiction reconciliation. The figure to file is the local one to the left.">
												VAT collected (EUR)
											</HoverHead>
										</TableHead>
										<TableHead>Remitted at</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{c.vatCollected.map((v, i) => (
										<TableRow key={i}>
											<TableCell className="font-mono">{v.mechanism}</TableCell>
											<TableCell>
												{v.state
													? `${v.countryCode}-${v.state}`
													: v.countryCode}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{vatBasisLabel(v.basis)}
											</TableCell>
											<TableCell className="font-mono text-muted-foreground">
												{v.currency}
											</TableCell>
											<TableCell className="text-right tabular-nums text-muted-foreground">
												{formatRate(v.rate)}
											</TableCell>
											<TableCell className="text-right">
												<Money
													cents={v.taxableBaseLocal}
													currency={v.currency}
												/>
											</TableCell>
											<TableCell className="text-right font-medium">
												<Money cents={v.amountLocal} currency={v.currency} />
											</TableCell>
											<TableCell className="text-right text-muted-foreground">
												<Money cents={v.amountEur} currency="EUR" />
											</TableCell>
											<TableCell className="text-muted-foreground">
												{v.remittedExternallyAt
													? new Date(
															v.remittedExternallyAt,
														).toLocaleDateString()
													: EMPTY_VALUE}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</DetailPanel>

					<DetailPanel
						title="Versions"
						icon={History}
						meta="Every generation is immutable. Finalising stamps the version the reviewer saw; a finalised version's workbook is never overwritten."
						flush
					>
						{c.versions.length === 0 ? (
							<p className="px-4 py-3 text-meta">No version generated yet.</p>
						) : (
							<Table className={FLUSH_TABLE_CLASS}>
								<TableHeader>
									<TableRow>
										<TableHead>Version</TableHead>
										<TableHead>Trigger</TableHead>
										<TableHead>Generated by</TableHead>
										<TableHead>Generated at</TableHead>
										<TableHead>Content hash</TableHead>
										<TableHead>Status</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{c.versions.map((v) => (
										<TableRow key={v.version}>
											<TableCell className="font-mono">
												v{v.version}
												{c.finalisedVersion === v.version ? (
													<span className="font-sans text-meta">
														{" "}
														(finalised)
													</span>
												) : null}
											</TableCell>
											<TableCell>{v.trigger}</TableCell>
											<TableCell className="text-muted-foreground">
												{v.generatedBy}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{new Date(v.generatedAt).toLocaleString()}
											</TableCell>
											<TableCell className="font-mono text-meta">
												{v.contentHash
													? `${v.contentHash.slice(0, 12)}…`
													: EMPTY_VALUE}
											</TableCell>
											<TableCell>
												{v.error ? (
													<StatusTag tone="bad" title={v.error}>
														Failed
													</StatusTag>
												) : v.completedAt ? (
													<StatusDot tone="ok">Complete</StatusDot>
												) : (
													<StatusDot tone="info">
														<Loader2 className="size-3 animate-spin" />
														In progress
													</StatusDot>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</DetailPanel>
				</div>
			</div>
		</TooltipProvider>
	)
}

function vatBasisLabel(basis: "goods" | "buyer_fee" | "seller_fee"): string {
	if (basis === "goods") return "Goods"
	if (basis === "buyer_fee") return "Buyer fee"
	return "Seller fee"
}

/**
 * Header text that reveals a tooltip on hover: a dotted underline and the
 * help cursor, no icon.
 */
function HoverHead({
	children,
	tooltip,
}: {
	children: React.ReactNode
	tooltip: string
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="cursor-help underline decoration-dotted decoration-meta underline-offset-4">
					{children}
				</span>
			</TooltipTrigger>
			<TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
		</Tooltip>
	)
}
